import api from '@/lib/api';
import axios from 'axios';

/**
 * Video-Meetings (Agora). Medien laufen Browser ↔ Agora; unser Backend
 * verteilt nur befristete RTC-Tokens. Gäste treten ohne Login bei —
 * verifiziert über Einladungs-PIN aus der E-Mail.
 */

export interface Meeting {
  id: string;
  organization_id: string;
  title: string | null;
  ticket_id: string | null;
  created_by: string | null;
  guest_code: string;
  status: 'active' | 'ended';
  created_at: string;
  first_name?: string | null;
  last_name?: string | null;
  organization_name?: string | null;
  invite_count?: number;
  /** Bin ich als Desk-Nutzer eingeladen? (Seitenleiste: „für dich") */
  is_invited?: boolean;
  members?: { name: string }[];
  guest_link?: string;
  live_count?: number;
  last_seen_at?: string | null;
}

export interface MeetingOrg { id: string; name: string }

export interface Colleague {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  role: string;
}

export interface RtcAccess {
  appId: string;
  channel: string;
  uid: number;
  token: string;
}

export interface CaptionsState {
  active: boolean;
  /** Gesprochene Sprache (z. B. "de-DE"). */
  source: string | null;
  /** Zielsprachen der Übersetzung. */
  targets: string[];
  /** Vom Server angebotene Sprachcodes. */
  languages: string[];
}

const meetingsService = {
  async list(): Promise<Meeting[]> {
    const { data } = await api.get('/meetings');
    return data.data;
  },

  async create(payload: { title?: string; ticket_id?: string; organization_id?: string; member_ids?: string[]; guest_emails?: string[] } = {}): Promise<Meeting & { invited?: { sent: string[]; failed: { email: string }[] } }> {
    const { data } = await api.post('/meetings', payload);
    return data.data;
  },

  /** Organisationen, in denen der Nutzer Mitglied ist (Berechtigungsgrenze). */
  async organizations(): Promise<MeetingOrg[]> {
    const { data } = await api.get('/meetings/organizations');
    return data.data;
  },

  /** Einladbare Desk-Nutzer einer Organisation. */
  async colleagues(organizationId: string): Promise<Colleague[]> {
    const { data } = await api.get('/meetings/colleagues', { params: { organizationId } });
    return data.data;
  },

  async getToken(id: string): Promise<{ rtc: RtcAccess; displayName: string; meeting: { id: string; title: string | null } }> {
    const { data } = await api.post(`/meetings/${id}/token`);
    return data.data;
  },

  async captionsState(id: string): Promise<CaptionsState> {
    const { data } = await api.get(`/meetings/${id}/captions`);
    return data.data;
  },

  /** Untertitel starten — läuft bereits ein Agent, wird er mit der neuen
      Sprachwahl ersetzt (Sprachwechsel mitten im Meeting). */
  async captionsStart(id: string, payload: { source: string; targets: string[] }): Promise<CaptionsState> {
    const { data } = await api.post(`/meetings/${id}/captions/start`, payload);
    return data.data;
  },

  async captionsStop(id: string): Promise<CaptionsState> {
    const { data } = await api.post(`/meetings/${id}/captions/stop`);
    return data.data;
  },

  /** Mehrere Adressen auf einmal — jede bekommt ihre eigene PIN-Mail. */
  async invite(id: string, emails: string[]): Promise<{ sent: string[]; failed: { email: string }[] }> {
    const { data } = await api.post(`/meetings/${id}/invite`, { emails });
    return data.data;
  },

  // ---- Zutritt (Meet-Verhalten): Anfrage stellen / freigeben ----

  /** Kapıyı çal: üye değilsem katılım isteği bırak. */
  async requestJoin(id: string): Promise<{ status: string }> {
    const { data } = await api.post(`/meetings/${id}/request-join`);
    return data.data;
  },

  /** Kendi isteğimin durumu: none | pending | approved | denied. */
  async requestStatus(id: string): Promise<{ status: string }> {
    const { data } = await api.get(`/meetings/${id}/request-status`);
    return data.data;
  },

  /** Bekleyen istekler (yalnız oda sahibine dolu döner). */
  async joinRequests(id: string): Promise<{ id: string; user_id: string; name: string | null }[]> {
    const { data } = await api.get(`/meetings/${id}/requests`);
    return data.data;
  },

  async decideRequest(id: string, reqId: string, decision: 'approve' | 'deny'): Promise<void> {
    await api.post(`/meetings/${id}/requests/${reqId}/${decision}`);
  },

  /** Kanaldaki uid → görünen ad eşlemesi (bildirimler + karo etiketleri). */
  async peers(id: string): Promise<Record<string, string>> {
    const { data } = await api.get(`/meetings/${id}/peers`);
    return data.data;
  },

  async guestPeers(code: string, email: string, pin: string): Promise<Record<string, string>> {
    const { data } = await axios.post(`/api/meetings/guest/${encodeURIComponent(code)}/peers`, { email, pin });
    return data.data;
  },

  /** Lebenszeichen aus dem Raum — leere Meetings werden serverseitig geschlossen. */
  async heartbeat(id: string, count: number): Promise<void> {
    await api.post(`/meetings/${id}/heartbeat`, { count });
  },

  async guestHeartbeat(code: string, count: number): Promise<void> {
    await axios.post(`/api/meetings/guest/${encodeURIComponent(code)}/heartbeat`, { count });
  },

  async end(id: string): Promise<void> {
    await api.post(`/meetings/${id}/end`);
  },

  // ---- Gast (ohne Login — deshalb rohes axios statt des Auth-Clients) ----

  async guestInfo(code: string): Promise<{ title: string | null; active: boolean }> {
    const { data } = await axios.get(`/api/meetings/guest/${encodeURIComponent(code)}`);
    return data.data;
  },

  async guestJoin(code: string, email: string, pin: string): Promise<{
    meeting: { id: string; title: string | null };
    displayName: string;
    rtc: RtcAccess;
  }> {
    const { data } = await axios.post(`/api/meetings/guest/${encodeURIComponent(code)}/join`, { email, pin });
    return data.data;
  },
};

export default meetingsService;

/** "a@b.de, c@d.de; e@f.de" → gültige Adressliste. */
export function parseEmails(raw: string): string[] {
  return [...new Set(
    raw.split(/[\s,;]+/).map((x) => x.trim().toLowerCase())
      .filter((x) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x)),
  )];
}
